import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Papa from "papaparse";
import { Upload, Plus, Trash2, Save, FilePlus, Search, Folder, X, Share2, Link as LinkIcon, Maximize2, Minimize2, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { StorageMeter, StorageUpgradeModal, useStorageUsage } from "../components/Storage";
import { consumePendingIncomingFile } from "../components/FileIntentBridge";
import ExportButton from "../components/ExportButton";
import { useAuth } from "../context/AuthContext";

const EMPTY = { headers: ["A", "B", "C"], rows: [["", "", ""]] };

export default function CsvEditor() {
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    // Free-tier guard. The editor route is open to everyone — anonymous users
    // and logged-in free-plan users get a read-only viewer; Pro users (active
    // subscription / trial / lifetime / admin) get full edit + cloud features.
    const isPro = !!user && user.is_paid;
    const upsell = useCallback(() => {
        toast.info("Upgrade to OpenCSV Pro to use this feature ($9.99/mo · 14-day free trial)");
        navigate("/pricing");
    }, [navigate]);
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState("untitled.csv");
    const [fileId, setFileId] = useState(null);
    const [headers, setHeaders] = useState(EMPTY.headers);
    const [rows, setRows] = useState(EMPTY.rows);
    const [dragOver, setDragOver] = useState(false);
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const [savedFiles, setSavedFiles] = useState([]);
    const [showFiles, setShowFiles] = useState(false);
    // Focus mode hides the global Navbar/Footer and the editor's secondary UI
    // (dropzone, hero text, search bar) so the grid takes ~95% of the viewport.
    // Mobile defaults to ON because the screen is small enough that Focus is
    // the right default. Choice persists across sessions in localStorage.
    const [focusMode, setFocusMode] = useState(() => {
        try {
            const stored = localStorage.getItem("opencsv:focus-mode");
            if (stored !== null) return stored === "1";
            // Default: ON for screens under 1024px (phones + small tablets)
            return typeof window !== "undefined" && window.innerWidth < 1024;
        } catch { return false; }
    });

    // Toggle focus mode: persist, and signal global Shell via a body class so
    // Navbar/Footer can hide themselves.
    useEffect(() => {
        try { localStorage.setItem("opencsv:focus-mode", focusMode ? "1" : "0"); } catch {/* ignore */ }
        const cls = "csv-focus-mode";
        if (focusMode) document.body.classList.add(cls);
        else document.body.classList.remove(cls);
        return () => document.body.classList.remove(cls);
    }, [focusMode]);

    // Esc to exit focus mode (desktop convenience).
    useEffect(() => {
        if (!focusMode) return;
        const onKey = (e) => { if (e.key === "Escape" && !e.target.matches("input,textarea")) setFocusMode(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [focusMode]);
    const [shareToken, setShareToken] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    // Naming prompt — shown when user tries to save with the default "untitled"
    // filename. Prevents the file list from filling up with untitled.csv copies.
    const [namePromptOpen, setNamePromptOpen] = useState(false);
    const [pendingName, setPendingName] = useState("");
    const [dirty, setDirty] = useState(false);
    const [storageUpgradeOpen, setStorageUpgradeOpen] = useState(false);
    const { usage: storageUsage, refresh: refreshStorage } = useStorageUsage();
    // Prompt the user before navigating away from unsaved changes (browser tab close / refresh).
    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (!dirty) return;
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    // Confirm before discarding unsaved edits. Returns true if user wants to proceed.
    const confirmDiscard = useCallback((actionLabel = "do this") => {
        if (!dirty) return true;
        return window.confirm(
            `You have unsaved changes to "${fileName}".\n\nClick OK to ${actionLabel} and DISCARD your edits.\nClick Cancel to stay and Save first.`
        );
    }, [dirty, fileName]);

    const fetchFiles = useCallback(async () => {
        try {
            const { data } = await api.get("/files");
            setSavedFiles(data);
        } catch (e) { /* not paid or auth issue */ }
    }, []);
    useEffect(() => { fetchFiles(); }, [fetchFiles]);

    // Open ?file=fileId on mount, OR pick up a CSV handed in via Android "Open with…"
    useEffect(() => {
        const fid = search.get("file");
        if (fid) {
            loadCloudFile(fid);
            return;
        }
        const incoming = consumePendingIncomingFile();
        if (incoming) {
            const parsed = Papa.parse(incoming.content, { skipEmptyLines: false });
            applyParsed(parsed.data, incoming.name);
            toast.success(`Opened ${incoming.name}`);
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If a new intent arrives while the editor is already mounted, swap to it.
    useEffect(() => {
        const onReplay = () => {
            if (!confirmDiscard("open this file")) return;
            const incoming = consumePendingIncomingFile();
            if (incoming) {
                const parsed = Papa.parse(incoming.content, { skipEmptyLines: false });
                applyParsed(parsed.data, incoming.name);
                toast.success(`Opened ${incoming.name}`);
            }
        };
        window.addEventListener("opencsv:replay-incoming-file", onReplay);
        return () => window.removeEventListener("opencsv:replay-incoming-file", onReplay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, fileName]);

    const loadCloudFile = async (id) => {
        if (!confirmDiscard("open this file")) return;
        try {
            const { data } = await api.get(`/files/${id}`);
            const parsed = Papa.parse(data.content, { skipEmptyLines: false });
            applyParsed(parsed.data, data.name, id);
            setShareToken(data.share_token || null);
            toast.success(`Opened ${data.name}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open file");
        }
    };

    const applyParsed = (data, name, id = null) => {
        if (!data || data.length === 0) {
            setHeaders(["A"]);
            setRows([[""]]);
        } else {
            const first = data[0] || [];
            const maxCols = data.reduce((m, r) => Math.max(m, r.length), first.length);
            const hdr = first.length === maxCols ? first.map((h, i) => h || `Col ${i + 1}`) : Array.from({ length: maxCols }, (_, i) => `Col ${i + 1}`);
            const body = first.length === maxCols ? data.slice(1) : data;
            const normalizedBody = body.map((r) => {
                const arr = Array.isArray(r) ? r.slice() : [];
                while (arr.length < maxCols) arr.push("");
                return arr.slice(0, maxCols);
            });
            setHeaders(hdr);
            setRows(normalizedBody.length ? normalizedBody : [Array(maxCols).fill("")]);
        }
        setFileName(name);
        setFileId(id);
        if (!id) setShareToken(null);
        setDirty(false);
    };

    const handleFile = (file) => {
        if (!file) return;
        if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
            toast.error("Please choose a .csv file");
            return;
        }
        if (!confirmDiscard("open a different file")) return;
        Papa.parse(file, {
            skipEmptyLines: false,
            complete: (res) => applyParsed(res.data, file.name),
            error: (err) => toast.error(`Parse failed: ${err.message}`),
        });
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        handleFile(file);
    };

    const isPlaceholderName = (n) => {
        const base = (n || "").trim().toLowerCase().replace(/\.csv$/, "");
        return !base || base === "untitled" || /^untitled[-_\s]?\d*$/.test(base);
    };

    const saveCloud = async () => {
        // If the filename is still the placeholder, open the name-prompt instead
        // of writing untitled.csv to the cloud. The actual write happens in
        // confirmSaveWithName once the user picks a real name.
        if (isPlaceholderName(fileName)) {
            setPendingName("");
            setNamePromptOpen(true);
            return;
        }
        await persistToCloud(fileName);
    };

    const confirmSaveWithName = async () => {
        const candidate = (pendingName || "").trim();
        if (isPlaceholderName(candidate)) {
            toast.error("Pick a name other than 'untitled'");
            return;
        }
        const name = candidate.endsWith(".csv") ? candidate : `${candidate}.csv`;
        setFileName(name);
        setNamePromptOpen(false);
        await persistToCloud(name);
    };

    const persistToCloud = async (name) => {
        try {
            setSaving(true);
            const csv = Papa.unparse([headers, ...rows]);
            const { data } = await api.post("/files", {
                name: name.endsWith(".csv") ? name : `${name}.csv`,
                content: csv,
                file_id: fileId,
            });
            setFileId(data.file_id);
            await fetchFiles();
            await refreshStorage();
            setDirty(false);
            toast.success("Saved to cloud");
        } catch (e) {
            const detail = e.response?.data?.detail;
            // Quota-exceeded -> open the upgrade modal instead of a toast
            if (detail && typeof detail === "object" && detail.code === "storage_quota_exceeded") {
                await refreshStorage();
                setStorageUpgradeOpen(true);
                toast.error("Cloud storage full — pick an upgrade or delete some files.");
            } else {
                toast.error(formatApiErrorDetail(detail) || "Save failed");
            }
        } finally {
            setSaving(false);
        }
    };

    const deleteCloudFile = async (id) => {
        if (!window.confirm("Delete this saved file?")) return;
        await api.delete(`/files/${id}`);
        await fetchFiles();
        await refreshStorage();
        if (fileId === id) {
            setFileId(null);
            toast.info("File removed from cloud");
        }
    };

    // Editing helpers
    const updateCell = (rIdx, cIdx, value) => {
        if (!isPro) { upsell(); return; }
        setRows((prev) => {
            const next = prev.map((r) => r.slice());
            next[rIdx][cIdx] = value;
            return next;
        });
        setDirty(true);
    };
    const updateHeader = (cIdx, value) => {
        if (!isPro) { upsell(); return; }
        setHeaders((prev) => prev.map((h, i) => (i === cIdx ? value : h)));
        setDirty(true);
    };

    // Multi-cell paste — when the user copies a range from Excel / Google Sheets
    // / another spreadsheet tool, the clipboard contains tab-separated rows. We
    // detect that, splat the grid starting at the focused cell, and grow the
    // table (rows + columns) as needed. Single-cell pastes fall through to the
    // browser's default paste so we don't break normal typing flow.
    const handlePaste = useCallback((rIdx, cIdx, isHeader, e) => {
        if (!isPro) { upsell(); e.preventDefault(); return; }
        const text = e.clipboardData?.getData("text/plain") || "";
        if (!text) return;
        const trimmed = text.replace(/\r/g, "").replace(/\n+$/, "");
        const hasTabs = trimmed.includes("\t");
        const hasNewlines = trimmed.includes("\n");
        if (!hasTabs && !hasNewlines) return; // single-cell value → default paste
        e.preventDefault();
        let grid;
        if (hasTabs) {
            grid = trimmed.split("\n").map((line) => line.split("\t"));
        } else {
            const parsed = Papa.parse(trimmed, { skipEmptyLines: false });
            grid = parsed.data || [];
        }
        if (grid.length === 0) return;
        const numRows = grid.length;
        const numCols = Math.max(...grid.map((r) => r.length));

        if (isHeader) {
            setHeaders((prev) => {
                const next = prev.slice();
                while (next.length < cIdx + numCols) next.push(`Col ${next.length + 1}`);
                for (let j = 0; j < numCols; j++) next[cIdx + j] = grid[0][j] ?? "";
                return next;
            });
            setRows((prev) => {
                const totalCols = Math.max(prev[0]?.length || 0, cIdx + numCols);
                let next = prev.map((r) => {
                    const row = r.slice();
                    while (row.length < totalCols) row.push("");
                    return row;
                });
                for (let i = 1; i < numRows; i++) {
                    const dr = i - 1;
                    while (next.length <= dr) next.push(Array(totalCols).fill(""));
                    for (let j = 0; j < numCols; j++) next[dr][cIdx + j] = grid[i][j] ?? "";
                }
                return next;
            });
        } else {
            setHeaders((prev) => {
                const next = prev.slice();
                while (next.length < cIdx + numCols) next.push(`Col ${next.length + 1}`);
                return next;
            });
            setRows((prev) => {
                const totalCols = Math.max(prev[0]?.length || 0, cIdx + numCols);
                let next = prev.map((r) => {
                    const row = r.slice();
                    while (row.length < totalCols) row.push("");
                    return row;
                });
                for (let i = 0; i < numRows; i++) {
                    const dr = rIdx + i;
                    while (next.length <= dr) next.push(Array(totalCols).fill(""));
                    for (let j = 0; j < numCols; j++) next[dr][cIdx + j] = grid[i][j] ?? "";
                }
                return next;
            });
        }
        setDirty(true);
        toast.success(`Pasted ${numRows} row${numRows > 1 ? "s" : ""} × ${numCols} col${numCols > 1 ? "s" : ""}`);
    }, [isPro, upsell]);
    const addRow = () => { if (!isPro) { upsell(); return; } setRows((p) => [...p, Array(headers.length).fill("")]); setDirty(true); };
    const removeRow = (i) => { if (!isPro) { upsell(); return; } setRows((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p)); setDirty(true); };
    const addColumn = () => {
        if (!isPro) { upsell(); return; }
        const name = `Col ${headers.length + 1}`;
        setHeaders((p) => [...p, name]);
        setRows((p) => p.map((r) => [...r, ""]));
        setDirty(true);
    };
    const removeColumn = (i) => {
        if (!isPro) { upsell(); return; }
        if (headers.length <= 1) return;
        setHeaders((p) => p.filter((_, idx) => idx !== i));
        setRows((p) => p.map((r) => r.filter((_, idx) => idx !== i)));
        setDirty(true);
    };

    const filteredRows = useMemo(() => {
        if (!query.trim()) return rows.map((r, i) => ({ r, i }));
        const q = query.toLowerCase();
        return rows.map((r, i) => ({ r, i })).filter(({ r }) => r.some((c) => String(c).toLowerCase().includes(q)));
    }, [rows, query]);

    const newBlank = () => {
        if (!confirmDiscard("start a new blank file")) return;
        setHeaders(["A", "B", "C"]);
        setRows([["", "", ""]]);
        setFileName("untitled.csv");
        setFileId(null);
        setShareToken(null);
        setDirty(false);
    };

    const openShare = async () => {
        if (!fileId) {
            toast.info("Save the file first, then share.");
            return;
        }
        try {
            const { data } = await api.post(`/files/${fileId}/share`);
            setShareToken(data.share_token);
            setShareOpen(true);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create share link");
        }
    };
    const revokeShare = async () => {
        if (!fileId) return;
        try {
            await api.delete(`/files/${fileId}/share`);
            setShareToken(null);
            setShareOpen(false);
            toast.success("Share link revoked");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke share");
        }
    };
    const shareUrl = shareToken ? `${window.location.origin}/s/${shareToken}` : "";
    const copyShareLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied to clipboard");
        } catch {
            toast.info(shareUrl);
        }
    };
    const copyShareTokenLink = async (token) => {
        const url = `${window.location.origin}/s/${token}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard");
        } catch {
            toast.info(url);
        }
    };
    const createShareForFile = async (id) => {
        try {
            const { data } = await api.post(`/files/${id}/share`);
            await fetchFiles();
            if (id === fileId) setShareToken(data.share_token);
            await copyShareTokenLink(data.share_token);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not share file");
        }
    };
    const revokeShareForFile = async (id) => {
        if (!window.confirm("Revoke this share link? Anyone with the URL will lose access.")) return;
        try {
            await api.delete(`/files/${id}/share`);
            await fetchFiles();
            if (id === fileId) setShareToken(null);
            toast.success("Share link revoked");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke share");
        }
    };

    return (
        <>
            <Helmet><title>{`${fileName || "Editor"} — OpenCSV`}</title></Helmet>
            <div className="border-b border-black/10 bg-white sticky top-16 z-30">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-3 flex flex-wrap gap-2 items-center">
                    <input
                        value={fileName}
                        onChange={(e) => { setFileName(e.target.value); setDirty(true); }}
                        className="font-mono text-sm border border-ink/15 px-2 py-1.5 focus-cobalt min-w-[180px]"
                        data-testid="filename-input"
                    />
                    {dirty && (
                        <span className="ui-heading text-[10px] uppercase tracking-widest px-2 py-0.5 border border-signal-orange/50 bg-signal-orange/10 text-signal-orange" data-testid="dirty-indicator" title="Unsaved changes">
                            ● Unsaved
                        </span>
                    )}
                    <span className="text-xs text-ink-muted hidden md:inline">{rows.length} rows · {headers.length} cols</span>
                    <div className="flex-1" />
                    <button onClick={() => fileInputRef.current?.click()} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm" data-testid="btn-open-file">
                        <Upload size={14} /> <span className="hidden sm:inline">Open</span>
                    </button>
                    <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="file-input" />
                    <button onClick={newBlank} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm" data-testid="btn-new-file">
                        <FilePlus size={14} /> <span className="hidden sm:inline">New</span>
                    </button>
                    {isPro && (
                        <button onClick={() => setShowFiles((s) => !s)} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm" data-testid="btn-cloud-files">
                            <Folder size={14} /> <span className="hidden sm:inline">Cloud</span>
                        </button>
                    )}
                    {isPro ? (
                        <ExportButton headers={headers} rows={rows} fileName={fileName} />
                    ) : (
                        <button onClick={upsell} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm text-ink-muted" data-testid="btn-export-locked" title="Pro feature">
                            <Lock size={12} /> <span className="hidden sm:inline">Export</span>
                        </button>
                    )}
                    {isPro && (
                        <button onClick={openShare} className={`btn-brutal inline-flex items-center gap-2 px-3 py-2 ui-heading text-sm border ${shareToken ? "bg-signal-green/10 border-signal-green text-signal-green" : "border-ink/20 bg-white"}`} data-testid="btn-share">
                            <Share2 size={14} /> <span className="hidden sm:inline">{shareToken ? "Shared" : "Share"}</span>
                        </button>
                    )}
                    {isPro && <StorageMeter usage={storageUsage} onClick={() => setStorageUpgradeOpen(true)} compact />}
                    <button onClick={() => setFocusMode((f) => !f)} className={`btn-brutal inline-flex items-center gap-2 px-3 py-2 ui-heading text-sm border ${focusMode ? "bg-ink text-white border-ink" : "border-ink/20 bg-white"}`} data-testid="btn-focus-toggle" title={focusMode ? "Exit focus mode (Esc)" : "Hide the rest of the page and use the full screen"}>
                        {focusMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        <span className="hidden sm:inline">{focusMode ? "Exit focus" : "Focus"}</span>
                    </button>
                    {isPro ? (
                        <button onClick={saveCloud} disabled={saving} className={`btn-brutal inline-flex items-center gap-2 px-3 py-2 ui-heading text-sm border disabled:opacity-60 ${dirty ? "bg-signal-orange text-white border-signal-orange" : "bg-cobalt text-white border-cobalt"}`} data-testid="btn-save-cloud">
                            <Save size={14} /> <span className="hidden sm:inline">{saving ? "Saving…" : (dirty ? "Save changes" : "Save")}</span>
                        </button>
                    ) : (
                        <button onClick={upsell} className="btn-brutal inline-flex items-center gap-2 px-3 py-2 ui-heading text-sm border bg-cobalt text-white border-cobalt" data-testid="btn-subscribe">
                            <Sparkles size={14} /> <span className="hidden sm:inline">Unlock Pro</span>
                        </button>
                    )}
                </div>
                {showFiles && (
                    <div className="border-t border-black/10 bg-paper" data-testid="cloud-files-panel">
                        <div className="mx-auto max-w-7xl px-4 md:px-8 py-3 flex flex-wrap gap-2 items-center">
                            <span className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Saved files</span>
                            <button onClick={() => setShowFiles(false)} className="ml-auto p-1" aria-label="Close"><X size={14} /></button>
                            <div className="basis-full" />
                            {savedFiles.length === 0 ? (
                                <span className="text-sm text-ink-muted">Nothing saved yet. Click Save to upload your first file.</span>
                            ) : (
                                <ul className="basis-full divide-y divide-black/10 border border-black/10 bg-white" data-testid="cloud-files-list">
                                    {savedFiles.map((f) => (
                                        <li key={f.file_id} className="flex items-center px-3 py-2 gap-2 flex-wrap" data-testid={`cloud-file-${f.file_id}`}>
                                            <span className="font-mono text-sm flex-1 min-w-0 truncate">{f.name}</span>
                                            {f.share_token && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] ui-heading uppercase tracking-widest bg-signal-green/10 text-signal-green border border-signal-green/40" data-testid={`shared-badge-${f.file_id}`}>
                                                    <Share2 size={10} /> shared
                                                </span>
                                            )}
                                            <span className="text-xs text-ink-muted hidden sm:inline">{f.size} b</span>
                                            <button onClick={() => loadCloudFile(f.file_id)} className="ui-heading text-xs px-2 py-1 border border-ink/15 hover:border-cobalt" data-testid={`open-cloud-${f.file_id}`}>Open</button>
                                            {f.share_token ? (
                                                <>
                                                    <button onClick={() => copyShareTokenLink(f.share_token)} className="ui-heading text-xs px-2 py-1 border border-ink/15 hover:border-cobalt" data-testid={`copy-share-${f.file_id}`}>Copy link</button>
                                                    <button onClick={() => revokeShareForFile(f.file_id)} className="ui-heading text-xs px-2 py-1 border border-signal-red/40 text-signal-red" data-testid={`revoke-share-${f.file_id}`}>Unshare</button>
                                                </>
                                            ) : (
                                                <button onClick={() => createShareForFile(f.file_id)} className="ui-heading text-xs px-2 py-1 border border-cobalt text-cobalt hover:bg-cobalt hover:text-white" data-testid={`share-cloud-${f.file_id}`}>Share</button>
                                            )}
                                            <button onClick={() => deleteCloudFile(f.file_id)} className="ui-heading text-xs px-2 py-1 border border-signal-red/40 text-signal-red" data-testid={`delete-cloud-${f.file_id}`}>Delete</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className={focusMode ? "mx-auto max-w-[1600px] px-2 md:px-4 py-2" : "mx-auto max-w-7xl px-4 md:px-8 py-6"}>
                {!isPro && !focusMode && (
                    <div className="mb-4 border-2 border-cobalt bg-cobalt/5 px-5 py-4 flex flex-wrap items-center gap-3" data-testid="free-tier-upsell-banner">
                        <Sparkles size={16} className="text-cobalt flex-shrink-0" />
                        <div className="flex-1 min-w-[240px]">
                            <div className="ui-heading text-[11px] uppercase tracking-widest text-cobalt">/ free viewer mode</div>
                            <p className="text-sm text-ink mt-0.5">
                                You're viewing in read-only mode. <span className="font-semibold">Unlock editing, cloud save & export presets</span> with OpenCSV Pro — start your 14-day free trial, no card required.
                            </p>
                        </div>
                        <Link to="/pricing" className="btn-brutal bg-cobalt text-white border border-cobalt px-4 py-2 ui-heading text-sm font-semibold inline-flex items-center gap-2" data-testid="free-tier-upgrade-link">
                            See Pro plans <Sparkles size={12} />
                        </Link>
                    </div>
                )}
                {!focusMode && (
                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={`border border-dashed border-ink/20 mb-4 px-4 py-3 flex items-center gap-3 text-sm text-ink-soft transition-colors duration-150 ${dragOver ? "dropzone-active" : "bg-white"}`}
                        data-testid="dropzone"
                    >
                        <Upload size={16} className="text-cobalt" />
                        <span>Drag & drop a .csv file here, or use Open above. Files load locally — never leaves your browser unless you Save. <span className="text-ink-muted">Tip: copy a range from Excel/Sheets and paste into any cell to splat in rows & columns at once.</span></span>
                    </div>
                )}

                <div className="flex flex-wrap gap-2 mb-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-muted" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search rows…" className="pl-7 pr-3 py-2 border border-ink/20 bg-white focus-cobalt text-sm w-56" data-testid="search-input" />
                    </div>
                    {isPro && (
                        <>
                            <button onClick={addRow} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm" data-testid="btn-add-row">
                                <Plus size={14} /> Row
                            </button>
                            <button onClick={addColumn} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm" data-testid="btn-add-col">
                                <Plus size={14} /> Column
                            </button>
                        </>
                    )}
                </div>

                <div className={`border border-black/10 overflow-auto subtle-scroll bg-white ${focusMode ? "max-h-[calc(100vh-110px)]" : "max-h-[68vh]"}`}>
                    <table className="csv-grid w-full">
                        <thead>
                            <tr>
                                <th className="row-num">#</th>
                                {headers.map((h, ci) => (
                                    <th key={ci} className="min-w-[140px]">
                                        <div className="flex items-center gap-1">
                                            <input value={h} onChange={(e) => updateHeader(ci, e.target.value)} onPaste={(e) => handlePaste(0, ci, true, e)} readOnly={!isPro} className={`font-bold w-full ${!isPro ? "cursor-not-allowed bg-paper/50" : ""}`} data-testid={`header-${ci}`} />
                                            {isPro && (
                                                <button onClick={() => removeColumn(ci)} className="text-ink-muted hover:text-signal-red" title="Remove column" data-testid={`remove-col-${ci}`}>
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </th>
                                ))}
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.map(({ r, i }) => (
                                <tr key={i}>
                                    <td className="row-num">{i + 1}</td>
                                    {r.map((c, ci) => (
                                        <td key={ci}>
                                            <input value={c ?? ""} onChange={(e) => updateCell(i, ci, e.target.value)} onPaste={(e) => handlePaste(i, ci, false, e)} readOnly={!isPro} className={!isPro ? "cursor-not-allowed bg-paper/30" : ""} data-testid={`cell-${i}-${ci}`} />
                                        </td>
                                    ))}
                                    <td className="text-center">
                                        {isPro && (
                                            <button onClick={() => removeRow(i)} className="text-ink-muted hover:text-signal-red p-1" title="Remove row" data-testid={`remove-row-${i}`}>
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredRows.length === 0 && (
                                <tr><td colSpan={headers.length + 2} className="p-6 text-center text-ink-muted text-sm">No rows match your search.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {shareOpen && shareToken && (
                <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="share-modal">
                    <div className="bg-white border border-ink w-full max-w-md p-6 shadow-brutal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display font-black text-2xl">Read-only share link</h3>
                            <button onClick={() => setShareOpen(false)} aria-label="Close"><X size={16} /></button>
                        </div>
                        <p className="text-sm text-ink-soft mb-4">Anyone with this link can view (but not edit) <span className="font-mono">{fileName}</span>. Revoke any time.</p>
                        <div className="flex items-stretch border border-ink/20" data-testid="share-link-row">
                            <input readOnly value={shareUrl} className="flex-1 font-mono text-xs px-3 py-2 bg-paper" data-testid="share-url-input" />
                            <button onClick={copyShareLink} className="px-4 ui-heading text-xs uppercase tracking-widest bg-ink text-white" data-testid="share-copy-button">
                                <LinkIcon size={12} className="inline mr-1" /> Copy
                            </button>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => window.open(shareUrl, "_blank")} className="btn-brutal flex-1 border border-ink/20 px-3 py-2 ui-heading text-sm" data-testid="share-preview-button">
                                Preview
                            </button>
                            <button onClick={revokeShare} className="btn-brutal flex-1 border border-signal-red/40 text-signal-red px-3 py-2 ui-heading text-sm" data-testid="share-revoke-button">
                                Revoke link
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <StorageUpgradeModal
                open={storageUpgradeOpen}
                onClose={() => setStorageUpgradeOpen(false)}
                currentUsage={storageUsage}
            />

            {namePromptOpen && (
                <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="name-prompt-modal">
                    <form onSubmit={(e) => { e.preventDefault(); confirmSaveWithName(); }} className="bg-white border border-ink w-full max-w-md shadow-brutal">
                        <div className="flex items-center justify-between border-b border-ink/10 px-5 py-3">
                            <h3 className="font-display font-black tracking-tighter text-xl">Name this file</h3>
                            <button type="button" onClick={() => setNamePromptOpen(false)} aria-label="Close" data-testid="name-prompt-close"><X size={16} /></button>
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-sm text-ink-soft mb-3">Give your file a real name before saving — that way it's easy to find later. "Untitled" is reserved as the default.</p>
                            <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft block">File name</label>
                            <input
                                value={pendingName}
                                onChange={(e) => setPendingName(e.target.value)}
                                autoFocus
                                placeholder="e.g. customers-2026, invoice-march, etc."
                                className="mt-1 w-full border border-ink/20 px-3 py-2.5 font-mono text-sm"
                                data-testid="name-prompt-input"
                            />
                            <p className="text-[11px] text-ink-muted mt-1">.csv will be added automatically if you don't include it.</p>
                        </div>
                        <div className="border-t border-ink/10 px-5 py-3 flex justify-end gap-2">
                            <button type="button" onClick={() => setNamePromptOpen(false)} className="border border-ink/20 px-3 py-2 text-sm" data-testid="name-prompt-cancel">Cancel</button>
                            <button type="submit" className="btn-brutal bg-cobalt text-white border border-cobalt px-4 py-2 ui-heading text-sm font-semibold" data-testid="name-prompt-save">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}
